import React from "react";
import { motion } from "framer-motion";
import { Phone, MapPin, Instagram, Facebook, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

const buildWhatsAppLink = (num) => {
  const clean = (num || "").replace(/[^\d]/g, "");
  const text = encodeURIComponent(
    "Namaste! I'd like to order Halli Chutney. Could you please share the details?"
  );
  return `https://wa.me/${clean}?text=${text}`;
};

export default function Contact({ business }) {
  const phone = business.phone || "+91 63633 64092";
  const wa = business.whatsapp || "+91 63633 64092";
  const location = business.location || "Kanakapura Main Road, Bangalore, Karnataka";
  const email = business.email || "hello@hallichutney.com";

  return (
    <section
      id="contact"
      className="py-20 md:py-32 bg-[#2F4F2F] text-stone-50 relative overflow-hidden"
      data-testid="contact-section"
    >
      <div className="absolute inset-0 grain opacity-30" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
          >
            <span className="font-hand text-2xl text-[#f2c15b]">— say namaste</span>
            <h2 className="mt-2 font-serif text-3xl md:text-4xl lg:text-5xl leading-tight">
              Order on WhatsApp,
              <br />
              <span className="italic text-[#f2c15b]">or just say hi.</span>
            </h2>
            <p className="mt-5 text-stone-200/90 leading-relaxed max-w-md">
              Prefer to order the old-fashioned way? Message us on WhatsApp — we&apos;ll
              take your order, confirm delivery, and pack your jars fresh.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href={buildWhatsAppLink(wa)}
                target="_blank"
                rel="noreferrer"
                data-testid="contact-whatsapp-btn"
              >
                <Button className="bg-[#25D366] hover:bg-[#1EA854] text-white rounded-full h-12 px-6 gap-2 transition-transform hover:-translate-y-0.5">
                  <MessageCircle className="w-4 h-4" />
                  Order on WhatsApp
                </Button>
              </a>
              <a href={`tel:${phone.replace(/\s/g, "")}`} data-testid="contact-phone-btn">
                <Button
                  variant="outline"
                  className="bg-transparent border-stone-50/40 text-stone-50 hover:bg-stone-50 hover:text-[#2F4F2F] rounded-full h-12 px-6 gap-2"
                >
                  <Phone className="w-4 h-4" />
                  Call {phone}
                </Button>
              </a>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="space-y-6"
          >
            <div className="bg-stone-50/5 border border-stone-50/15 backdrop-blur-md rounded-2xl p-6">
              <div className="flex gap-4">
                <div className="w-10 h-10 rounded-full bg-[#f2c15b]/20 text-[#f2c15b] grid place-items-center">
                  <Phone className="w-4 h-4" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-stone-300">Phone</div>
                  <div className="font-serif text-xl mt-1" data-testid="contact-phone-value">
                    {phone}
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-stone-50/5 border border-stone-50/15 backdrop-blur-md rounded-2xl p-6">
              <div className="flex gap-4">
                <div className="w-10 h-10 rounded-full bg-[#f2c15b]/20 text-[#f2c15b] grid place-items-center">
                  <MapPin className="w-4 h-4" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-stone-300">Location</div>
                  <div className="font-serif text-xl mt-1" data-testid="contact-location-value">
                    {location}
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-stone-50/5 border border-stone-50/15 backdrop-blur-md rounded-2xl p-6">
              <div className="text-xs uppercase tracking-widest text-stone-300 mb-3">
                Follow the kitchen
              </div>
              <div className="flex gap-3">
                <a
                  href="https://instagram.com"
                  target="_blank"
                  rel="noreferrer"
                  className="w-11 h-11 rounded-full bg-stone-50/10 hover:bg-[#f2c15b] hover:text-[#2F4F2F] grid place-items-center transition-colors"
                  data-testid="social-instagram"
                  aria-label="Instagram"
                >
                  <Instagram className="w-4 h-4" />
                </a>
                <a
                  href="https://facebook.com"
                  target="_blank"
                  rel="noreferrer"
                  className="w-11 h-11 rounded-full bg-stone-50/10 hover:bg-[#f2c15b] hover:text-[#2F4F2F] grid place-items-center transition-colors"
                  data-testid="social-facebook"
                  aria-label="Facebook"
                >
                  <Facebook className="w-4 h-4" />
                </a>
                <a
                  href={buildWhatsAppLink(wa)}
                  target="_blank"
                  rel="noreferrer"
                  className="w-11 h-11 rounded-full bg-stone-50/10 hover:bg-[#f2c15b] hover:text-[#2F4F2F] grid place-items-center transition-colors"
                  data-testid="social-whatsapp"
                  aria-label="WhatsApp"
                >
                  <MessageCircle className="w-4 h-4" />
                </a>
              </div>
              <div className="text-xs text-stone-300 mt-4">
                Email: <span data-testid="contact-email-value">{email}</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
